/* RESPONSIBLE TEAM: team-automation-foundations */

import { AiAgentEventType } from 'embercom/objects/inbox/renderable/ai-agent-event';

export type AiAgentEventData = {
  [AiAgentEventType.OtpEmailTriggeredForVerification]?: {
    title: string;
    actionId: number;
  };
  [AiAgentEventType.AiCategoriesEscalation]?: {
    ruleName: string;
  };
  [AiAgentEventType.AiAgentIgnoringUserMessage]?: {
    reason: string;
  };
  [AiAgentEventType.AiCategoriesReasoning]?: {
    reasoning: string;
  };
  [AiAgentEventType.AiAgentWorkflowStarted]?: {
    title: string;
    id: number;
    rulesetId: number;
    reasoning?: string;
  };
  [AiAgentEventType.AiAgentWorkflowFinished]?: {
    title: string;
    id: number;
    rulesetId: number;
  };
  [AiAgentEventType.AiAgentWorkflowFailed]?: {
    title: string;
    id: number;
    rulesetId: number;
    reasoning?: string;
  };
  [AiAgentEventType.AiAgentWorkflowStopped]?: {
    title: string;
    id: number;
    rulesetId: number;
  };
  [AiAgentEventType.SdrRoutingDecision]?: {
    routingOutcomeIdentifier: string;
    routingOutcomeReason: string;
    routingOutcomeId: number;
    routingOutcomeTitle: string;
  };
  [AiAgentEventType.AiAgentSetAttributes]?: {
    attributes: Array<{
      name: string;
      value: string;
    }>;
  };
  [AiAgentEventType.AiAgentTaskStepReasoning]?: {
    stepNumber: string;
    stepDescription: string;
    reason: string;
    reasonedStepIds: Array<{
      sectionName: string;
      stepNumber: string;
    }>;
    transactionId: string;
    procedurePlannerRunId: string;
  };
  [AiAgentEventType.AiAgentConditionalEvaluation]?: {
    selectedBranchNumber: string;
    stepNumber: string;
    stepType: string;
    skippedCondition: string;
  };
  [AiAgentEventType.AiAgentContentRetrieval]?: {
    stepNumber: string;
    query: string;
    documentCount: number;
    passageCount: number;
    retrievedContent: Array<{
      entityTypeId: number;
      entityId: number;
      title: string;
      matched: boolean;
    }>;
  };
  [AiAgentEventType.AiAgentSkippedSteps]?: {
    skippedSteps: Array<{
      sectionName: string;
      stepNumber: string;
    }>;
  };
  [AiAgentEventType.AiAgentCodeConditionError]?: {
    stepNumber: string;
    condition: string;
    errorMessage: string;
  };
  [AiAgentEventType.AiAgentGotoSection]?: {
    sectionName: string;
  };
  [AiAgentEventType.AiAgentEndSection]?: {
    sectionName: string;
    returnedToSectionName: string;
  };
  [AiAgentEventType.ImpersonatedUserDataUpdateNotice]?: {
    userDisplayName: string;
  };
  [AiAgentEventType.AiAgentFollowUpScheduled]?: {
    rulesetId?: number;
    stepId?: number;
    source?: 'workflow' | 'simple_deploy';
  };
};

export type AiAgentEventDataWireFormat = {
  [K in keyof AiAgentEventData]: KeysToSnakeCase<AiAgentEventData[K]>;
};

export class AiAgentEventDataBuilder {
  static deserialize(json: AiAgentEventDataWireFormat): AiAgentEventData {
    let data: AiAgentEventData = {};

    if (json.otp_email_triggered_for_verification) {
      data[AiAgentEventType.OtpEmailTriggeredForVerification] = {
        title: json.otp_email_triggered_for_verification.title,
        actionId: json.otp_email_triggered_for_verification.action_id,
      };
    }

    if (json.ai_categories_escalation) {
      data[AiAgentEventType.AiCategoriesEscalation] = {
        ruleName: json.ai_categories_escalation.rule_name,
      };
    }

    if (json.ai_agent_ignoring_user_message) {
      data[AiAgentEventType.AiAgentIgnoringUserMessage] = {
        reason: json.ai_agent_ignoring_user_message.reason,
      };
    }

    if (json.ai_categories_reasoning) {
      data[AiAgentEventType.AiCategoriesReasoning] = {
        reasoning: json.ai_categories_reasoning.reasoning,
      };
    }

    if (json.ai_agent_workflow_started) {
      let value: AiAgentEventData[AiAgentEventType.AiAgentWorkflowStarted] = {
        title: json.ai_agent_workflow_started.title,
        id: json.ai_agent_workflow_started.id,
        rulesetId: json.ai_agent_workflow_started.ruleset_id,
      };
      if (json.ai_agent_workflow_started.reasoning !== undefined) {
        value.reasoning = json.ai_agent_workflow_started.reasoning;
      }
      data[AiAgentEventType.AiAgentWorkflowStarted] = value;
    }

    if (json.ai_agent_workflow_finished) {
      data[AiAgentEventType.AiAgentWorkflowFinished] = {
        title: json.ai_agent_workflow_finished.title,
        id: json.ai_agent_workflow_finished.id,
        rulesetId: json.ai_agent_workflow_finished.ruleset_id,
      };
    }

    if (json.ai_agent_workflow_stopped) {
      data[AiAgentEventType.AiAgentWorkflowStopped] = {
        title: json.ai_agent_workflow_stopped.title,
        id: json.ai_agent_workflow_stopped.id,
        rulesetId: json.ai_agent_workflow_stopped.ruleset_id,
      };
    }

    if (json.ai_agent_workflow_failed) {
      let aiAgentWorkflowData = json.ai_agent_workflow_failed;
      data[AiAgentEventType.AiAgentWorkflowFailed] = {
        title: aiAgentWorkflowData.title,
        id: aiAgentWorkflowData.id,
        rulesetId: aiAgentWorkflowData.ruleset_id,
        ...(aiAgentWorkflowData.reasoning !== undefined && {
          reasoning: aiAgentWorkflowData.reasoning,
        }),
      };
    }

    if (json.sdr_routing_decision) {
      data[AiAgentEventType.SdrRoutingDecision] = {
        routingOutcomeIdentifier: json.sdr_routing_decision.routing_outcome_identifier,
        routingOutcomeReason: json.sdr_routing_decision.routing_outcome_reason,
        routingOutcomeId: json.sdr_routing_decision.routing_outcome_id,
        routingOutcomeTitle: json.sdr_routing_decision.routing_outcome_title,
      };
    }

    if (json.ai_agent_set_attributes) {
      data[AiAgentEventType.AiAgentSetAttributes] = {
        attributes: json.ai_agent_set_attributes.attributes,
      };
    }

    if (json.ai_agent_task_step_reasoning) {
      data[AiAgentEventType.AiAgentTaskStepReasoning] = {
        stepNumber: json.ai_agent_task_step_reasoning.step_number,
        stepDescription: json.ai_agent_task_step_reasoning.step_description,
        reason: json.ai_agent_task_step_reasoning.reason,
        reasonedStepIds: (
          json.ai_agent_task_step_reasoning.reasoned_step_ids as Array<Record<string, string>>
        ).map((item) => ({
          sectionName: item.section_name,
          stepNumber: item.step_number,
        })),
        transactionId: json.ai_agent_task_step_reasoning.transaction_id,
        procedurePlannerRunId: json.ai_agent_task_step_reasoning.procedure_planner_run_id,
      };
    }

    if (json.ai_agent_conditional_evaluation) {
      data[AiAgentEventType.AiAgentConditionalEvaluation] = {
        selectedBranchNumber: json.ai_agent_conditional_evaluation.selected_branch_number,
        stepNumber: json.ai_agent_conditional_evaluation.step_number,
        stepType: json.ai_agent_conditional_evaluation.step_type,
        skippedCondition: json.ai_agent_conditional_evaluation.skipped_condition,
      };
    }

    if (json.ai_agent_content_retrieval) {
      data[AiAgentEventType.AiAgentContentRetrieval] = {
        stepNumber: json.ai_agent_content_retrieval.step_number,
        query: json.ai_agent_content_retrieval.query,
        documentCount: json.ai_agent_content_retrieval.document_count,
        passageCount: json.ai_agent_content_retrieval.passage_count,
        retrievedContent: (
          json.ai_agent_content_retrieval.retrieved_content as Array<Record<string, unknown>>
        ).map((item) => ({
          entityTypeId: item.entity_type_id as number,
          entityId: item.entity_id as number,
          title: item.title as string,
          matched: item.matched as boolean,
        })),
      };
    }

    if (json.ai_agent_skipped_steps) {
      data[AiAgentEventType.AiAgentSkippedSteps] = {
        skippedSteps: (
          json.ai_agent_skipped_steps.skipped_steps as Array<Record<string, string>>
        ).map((item) => ({
          sectionName: item.section_name,
          stepNumber: item.step_number,
        })),
      };
    }

    if (json.ai_agent_code_condition_error) {
      data[AiAgentEventType.AiAgentCodeConditionError] = {
        stepNumber: json.ai_agent_code_condition_error.step_number,
        condition: json.ai_agent_code_condition_error.condition,
        errorMessage: json.ai_agent_code_condition_error.error_message,
      };
    }

    if (json.ai_agent_goto_section) {
      data[AiAgentEventType.AiAgentGotoSection] = {
        sectionName: json.ai_agent_goto_section.section_name,
      };
    }

    if (json.ai_agent_end_section) {
      data[AiAgentEventType.AiAgentEndSection] = {
        sectionName: json.ai_agent_end_section.section_name,
        returnedToSectionName: json.ai_agent_end_section.returned_to_section_name,
      };
    }

    if (json.impersonated_user_data_update_notice) {
      data[AiAgentEventType.ImpersonatedUserDataUpdateNotice] = {
        userDisplayName: json.impersonated_user_data_update_notice.user_display_name,
      };
    }

    if (json.ai_agent_follow_up_scheduled) {
      data[AiAgentEventType.AiAgentFollowUpScheduled] = {
        rulesetId: json.ai_agent_follow_up_scheduled.ruleset_id,
        stepId: json.ai_agent_follow_up_scheduled.step_id,
        source: json.ai_agent_follow_up_scheduled.source,
      };
    }

    return data;
  }
}
